import type { ReactNode } from "react";
import { useT } from "@/contexts/I18nContext";
import { failureKey } from "@/services/errors";
import type { AppError } from "@/services/errors";
import { Button } from "./Button";

/**
 * The three states of a read, rendered once.
 *
 * A failed call must not look like an empty result. "You have no documents" and
 * "we could not ask about your documents" are different sentences, and only one
 * of them is ever true at a time.
 */
export function Loaded<T>({
  state,
  children,
}: {
  state: {
    data: T | undefined;
    loading: boolean;
    failed: boolean;
    error?: AppError | undefined;
    reload: () => void;
  };
  children: (data: T) => ReactNode;
}) {
  const t = useT();

  if (state.loading) {
    return <p className="py-12 text-center text-[14px] text-ink-muted">{t("common.loading")}</p>;
  }

  if (state.failed || state.data === undefined) {
    return (
      <div className="py-12 text-center">
        {/*
          Which failure it was, in words. "Check your connection" over a 500
          from our own server sends a teacher to restart a router they have no
          business touching (live run, 2026-09-09).
        */}
        <p className="mx-auto max-w-md text-[14px] text-ink-muted">
          {t(failureKey(state.error))}
        </p>
        <Button variant="secondary" onClick={state.reload} className="mt-4">
          {t("common.retry")}
        </Button>
      </div>
    );
  }

  return <>{children(state.data)}</>;
}
